import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { accounts } from '../../data/accounts';
import { getItems, saveItems } from '../../data/items';

const itemTypes = ['Goods', 'Service'];
const units = ['Nos', 'Piece', 'Meter', 'Kg', 'Box'];

const ItemForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: '',
    sku: '',
    type: 'Goods',
    description: '',
    purchaseDescription: '',
    costPrice: '',
    sellingPrice: '',
    account: 'Sales',
    preferredVendor: '',
    purchaseAccount: 'Inventory',
    unit: 'Nos',
    stock: '',
  });

  const existingItems = useMemo(() => getItems(), []);

  useEffect(() => {
    if (!isEdit) return;

    const selected = existingItems.find((item) => String(item.id) === id);
    if (selected) {
      setForm({
        ...selected,
        costPrice: selected.costPrice ?? '',
        sellingPrice: selected.sellingPrice ?? '',
        stock: selected.stock ?? '',
      });
    }
  }, [existingItems, id, isEdit]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const nextItem = {
      ...form,
      id: isEdit ? Number(id) : Date.now(),
      costPrice: Number(form.costPrice) || 0,
      sellingPrice: Number(form.sellingPrice) || 0,
      stock: Number(form.stock) || 0,
    };

    const nextItems = isEdit
      ? existingItems.map((item) => (String(item.id) === id ? nextItem : item))
      : [nextItem, ...existingItems];

    saveItems(nextItems);
    navigate('/dashboard/items');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
        <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/items')}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          {isEdit ? 'Edit Item' : 'New Item'}
        </Typography>
      </Stack>

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Basic Information
            </Typography>
            <Stack spacing={2} sx={{ mb: 4 }}>
              <TextField label="Name" name="name" value={form.name} onChange={handleChange} required />
              <TextField select label="Type" name="type" value={form.type} onChange={handleChange}>
                {itemTypes.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select label="Unit" name="unit" value={form.unit} onChange={handleChange}>
                {units.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Sales Information
            </Typography>
            <Stack spacing={2} sx={{ mb: 4 }}>
              <TextField label="Selling Price" name="sellingPrice" type="text" value={form.sellingPrice} onChange={handleChange} required />
              <TextField label="Description" name="description" value={form.description} onChange={handleChange} multiline minRows={3} />
              <TextField select label="Income Account" name="account" value={form.account} onChange={handleChange}>
                {accounts.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Purchase Information
            </Typography>
            <Stack spacing={2} sx={{ mb: 4 }}>
              <TextField label="Purchase Description" name="purchaseDescription" value={form.purchaseDescription} onChange={handleChange} />
              <TextField label="Cost Price" name="costPrice" type="text" value={form.costPrice} onChange={handleChange} required />
              <TextField select label="Purchase Account" name="purchaseAccount" value={form.purchaseAccount} onChange={handleChange}>
                {accounts.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField label="Preferred Vendor" name="preferredVendor" value={form.preferredVendor} onChange={handleChange} />
              <TextField label="Stock" name="stock" type="text" value={form.stock} onChange={handleChange} />
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button type="submit" variant="contained" startIcon={<SaveIcon />}>
                Save
              </Button>
              <Button variant="outlined" onClick={() => navigate('/dashboard/items')}>
                Cancel
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ItemForm;
